import type { KeyboardEvent, ReactNode } from 'react'

/**
 * Two or three mutually exclusive option cards: a title, a one-line description
 * and a mono duration line. A radiogroup, so the keyboard moves between them
 * and a screen reader hears which is chosen.
 *
 * Ported from the design system's `components/record/ChoiceCards.jsx` (added
 * 2026-10-02 for the paired-session concept, §3 component map) by the same
 * rule as every capture component: the geometry and the states are the
 * prototype's, drawn with this app's tokens in `capture.css` rather than with
 * the prototype's inline styles. Used on Record · Ready for the session choice
 * (Sprint 1.9.13 W4); eyes closed stays selected by default, and the choice
 * exists only while `PAIRED_SESSION_ENABLED`.
 */
export interface ChoiceOption<V extends string> {
  value: V
  title: string
  description?: string
  /** Mono line under the description: "6 minutes · 2 blocks". */
  meta?: string
  children?: ReactNode
}

export function ChoiceCards<V extends string>({
  options,
  value,
  onChange,
  label,
  pressed = false,
}: {
  options: readonly ChoiceOption<V>[]
  value: V
  onChange: (value: V) => void
  /** The group's accessible name. */
  label: string
  /** EXP-002 uses toggle buttons; omitted, the paired session's DOM is unchanged. */
  pressed?: boolean
}) {
  const moveChoice = (event: KeyboardEvent<HTMLDivElement>) => {
    const current = event.target
    if (!(current instanceof HTMLButtonElement)) return
    let next: Element | null
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = current.nextElementSibling ?? event.currentTarget.firstElementChild
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        next = current.previousElementSibling ?? event.currentTarget.lastElementChild
        break
      case 'Home':
        next = event.currentTarget.firstElementChild
        break
      case 'End':
        next = event.currentTarget.lastElementChild
        break
      default:
        return
    }
    event.preventDefault()
    if (next instanceof HTMLButtonElement) {
      next.focus()
      next.click()
    }
  }

  return (
    <div className="choice" role={pressed ? 'group' : 'radiogroup'} aria-label={label} onKeyDown={pressed ? moveChoice : undefined}>
      {options.map((o) => {
        const on = o.value === value
        return (
          <button
            key={o.value}
            type="button"
            role={pressed ? undefined : 'radio'}
            aria-checked={pressed ? undefined : on}
            aria-pressed={pressed ? on : undefined}
            className={on ? 'choice__opt choice__opt--on' : 'choice__opt'}
            onClick={() => onChange(o.value)}
          >
            <span className="choice__title">{o.title}</span>
            {o.description !== undefined && <span className="choice__desc">{o.description}</span>}
            {o.meta !== undefined && <span className="choice__meta">{o.meta}</span>}
            {o.children}
          </button>
        )
      })}
    </div>
  )
}
